/**
 * How far the on-screen keyboard reaches into a view: the one measurement `DialogScrim` and the `Sheet`'s two `Modal`
 * forms (`avoidKeyboard`) keep their content clear of the keyboard by. A library hook (COMPONENT_LIBRARY.md §4),
 * promoted from `DialogScrim` on 2026-11-03 (RESERVATIONS_V2_PLAN.md decision 189), which reads it unchanged.
 *
 * ```tsx
 * const { overlap, onLayout } = useKeyboardOverlap();
 * <View onLayout={onLayout} style={{ paddingBottom: overlap }}>…</View>
 * ```
 *
 * `onLayout` goes on a view at the window's origin (a `Modal`'s root) or inside one whose bottom edge does not move
 * with the answer: it records that view's bottom edge, and the overlap is how far below the keyboard's top that edge
 * sits. Padding by the answer, or growing a spacer at the end of the column to it, keeps the edge where it is, so
 * the measurement never feeds back into itself.
 *
 * 🔴 **Why a `Modal` needs the keyboard's TOP, not its height.** On Android a `Modal`'s window is drawn edge to edge
 * (React Native 0.87 with `edgeToEdgeEnabled`), behind the navigation bar, while the keyboard height React Native
 * reports leaves that bar out (`ReactRootView.checkForKeyboardEvents`: the IME's inset minus the system bars'). Padding
 * by the height would leave the bar's strip of the dialog under the keyboard. The app's own window is inside its
 * root safe area, so a view there (the gorhom portal sheet) pads by `useKeyboardInset()`'s height instead.
 *
 * ⚠️ **Not React Native's `KeyboardAvoidingView`, deliberately.** That one learns about a keyboard only from the NEXT
 * show event, so a dialog that opens while one is already up would sit under it until the keyboard moved. This reads
 * `Keyboard.metrics()` on mount as well (`DialogScrim` explains the consent dialog that opens exactly that way).
 *
 * Web: react-native-web's `Keyboard` never reports one (`isVisible()` is `false` and no event fires), so the overlap
 * is always 0 there: the browser owns its keyboard.
 */
import { useCallback, useEffect, useState } from "react";
import { Keyboard, LayoutAnimation, Platform } from "react-native";
import type { KeyboardEvent, LayoutChangeEvent } from "react-native";

/** What the hook answers: the overlap, and the layout handler that measures the view it is taken against. */
export interface KeyboardOverlap {
  /** How far, in points, the keyboard reaches above the measured view's bottom edge; 0 with no keyboard. */
  overlap: number;
  /** Put on the measured view: records its bottom edge. */
  onLayout: (event: LayoutChangeEvent) => void;
}

/** How far the keyboard reaches into the view `onLayout` measures. */
export function useKeyboardOverlap(): KeyboardOverlap {
  const keyboardTop = useKeyboardTop();
  const [bottomEdge, setBottomEdge] = useState<number | null>(null);
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const { y, height } = event.nativeEvent.layout;
    setBottomEdge(y + height);
  }, []);
  // the same sum KeyboardAvoidingView does
  const overlap = keyboardTop == null || bottomEdge == null ? 0 : Math.max(0, bottomEdge - keyboardTop);
  return { overlap, onLayout };
}

/**
 * `Keyboard`'s two read-outs, reached as OPTIONAL. They arrived in React Native 0.71's types, and `apps/web` still
 * compiles this file against the hoisted `@types/react-native@0.70`, which has neither; react-native-web has no
 * `metrics` at runtime either.
 */
type KeyboardReadout = {
  isVisible?: () => boolean;
  metrics?: () => { screenY: number } | null | undefined;
};

/** The keyboard's top edge in screen coordinates, or `null` while none is showing. */
function useKeyboardTop(): number | null {
  // Seeded from a keyboard that is ALREADY up: see the header.
  const [top, setTop] = useState<number | null>(() => {
    const keyboard = Keyboard as typeof Keyboard & KeyboardReadout;
    return keyboard.isVisible?.() ? (keyboard.metrics?.()?.screenY ?? null) : null;
  });

  useEffect(() => {
    // The pair KeyboardAvoidingView listens to: iOS announces a change before animating it, Android only once it
    // has happened.
    const [showEvent, hideEvent] =
      Platform.OS === "ios" ? (["keyboardWillShow", "keyboardWillHide"] as const) : (["keyboardDidShow", "keyboardDidHide"] as const);

    const subscriptions = [
      Keyboard.addListener(showEvent, (event) => {
        animateWith(event);
        setTop(event.endCoordinates.screenY);
      }),
      Keyboard.addListener(hideEvent, (event) => {
        animateWith(event);
        setTop(null);
      }),
    ];
    return () => subscriptions.forEach((subscription) => subscription.remove());
  }, []);

  return top;
}

/**
 * Move with the keyboard where the platform says how (iOS), exactly as KeyboardAvoidingView does. Android's events
 * carry no duration, so there the view simply settles once the keyboard has.
 */
function animateWith({ duration, easing }: KeyboardEvent) {
  if (!duration || !easing) return;
  // RCTLayoutAnimation's minimum accepted duration is 10ms.
  const ms = Math.max(duration, 10);
  LayoutAnimation.configureNext({
    duration: ms,
    update: { duration: ms, type: LayoutAnimation.Types[easing] || "keyboard" },
  });
}
